/*
 * «О кластере»: описание, окружение, платформа и зоны, домен, версия Kubernetes и runtime
 * (из API кластера), ingress IP, владелец, система, тэги. Заменяет стандартный AboutCard.
 */
import { ReactNode } from 'react';
import { Chip, Grid, Typography, makeStyles } from '@material-ui/core';
import { InfoCard, Link } from '@backstage/core-components';
import {
  parseEntityRef,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  EntityRefLink,
  EntityRefLinks,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import {
  CLUSTER_DOMAIN_ANNOTATION,
  CLUSTER_ENV_ANNOTATION,
  CLUSTER_TARGET_ANNOTATION,
  CLUSTER_TARGET_LABELS,
  annotation,
} from '../../../lib/viva';
import { ZONE_LABEL } from '../../../lib/k8s';
import { useClusterData } from './useClusterData';

export const INGRESS_IP_ANNOTATION = 'viva.company.com/ingress-ip';

const useStyles = makeStyles(theme => ({
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: 2,
  },
  value: { fontSize: 14, overflowWrap: 'anywhere' },
  mono: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  muted: { color: theme.palette.text.secondary },
}));

const Field = ({
  label,
  children,
  full,
}: {
  label: string;
  children: ReactNode;
  full?: boolean;
}) => {
  const classes = useStyles();
  return (
    <Grid item xs={12} sm={full ? 12 : 6}>
      <Typography className={classes.label}>{label}</Typography>
      <div className={classes.value}>{children}</div>
    </Grid>
  );
};

export const ClusterAboutCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const data = useClusterData();
  const dash = <span className={classes.muted}>—</span>;

  const env = annotation(entity, CLUSTER_ENV_ANNOTATION);
  const target = annotation(entity, CLUSTER_TARGET_ANNOTATION);
  const domain = annotation(entity, CLUSTER_DOMAIN_ANNOTATION);
  const ingressIp = annotation(entity, INGRESS_IP_ANNOTATION);
  const zones = [
    ...new Set(
      (data.value?.nodes ?? []).map(n => n.labels[ZONE_LABEL]).filter(Boolean),
    ),
  ].sort();
  const platform = [
    target ? CLUSTER_TARGET_LABELS[target] ?? target : undefined,
    ...zones,
  ]
    .filter(Boolean)
    .join(' · ');
  const runtime = data.value?.runtime?.replace('://', ' ');
  const kubernetes = [data.value?.version, runtime].filter(Boolean).join(', ');

  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  const ownerRef = entity.spec?.owner
    ? stringifyEntityRef(
        parseEntityRef(String(entity.spec.owner), {
          defaultKind: 'group',
          defaultNamespace: 'default',
        }),
      )
    : undefined;
  const systemRef = entity.spec?.system
    ? stringifyEntityRef(
        parseEntityRef(String(entity.spec.system), {
          defaultKind: 'system',
          defaultNamespace: 'default',
        }),
      )
    : undefined;

  let owner = dash;
  if (owners.length > 0) {
    owner = <EntityRefLinks entityRefs={owners} defaultKind="group" />;
  } else if (ownerRef) {
    owner = <EntityRefLink entityRef={ownerRef} />;
  }

  return (
    <InfoCard title="О кластере">
      <Grid container spacing={2}>
        <Field label="Описание" full>
          {entity.metadata.description || dash}
        </Field>
        <Field label="Окружение">{env || dash}</Field>
        <Field label="Платформа">{platform || dash}</Field>
        <Field label="Домен">
          {domain ? <Link to={`https://${domain}`}>{domain}</Link> : dash}
        </Field>
        <Field label="Kubernetes">
          {kubernetes ||
            (data.loading ? (
              <span className={classes.muted}>загрузка…</span>
            ) : (
              dash
            ))}
        </Field>
        <Field label="Ingress IP">
          {ingressIp ? <span className={classes.mono}>{ingressIp}</span> : dash}
        </Field>
        <Field label="Владелец">{owner}</Field>
        <Field label="Система">
          {systemRef ? <EntityRefLink entityRef={systemRef} /> : dash}
        </Field>
        <Field label="Тэги">
          {entity.metadata.tags?.length ? (
            <div className={classes.chips}>
              {entity.metadata.tags.map(t => (
                <Chip key={t} label={t} size="small" />
              ))}
            </div>
          ) : (
            dash
          )}
        </Field>
      </Grid>
    </InfoCard>
  );
};
